// Block D: Import CSV (Business). Choose a file, see every row and what will happen to it, then
// import, then the server's own counts. Nothing is saved before "Import".
//
// The rows go up in small batches (utils/contactCsv importBatches): the edge refuses a request
// body over about 8 KB, so a whole file in one request would be refused before it reached Aidey.
// Each batch is de-duplicated by the server against everything already saved, earlier batches
// included. If a batch fails, the import stops there and says exactly how far it got.
import React, { useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';
import { Modal, Button, StatusPill, Banner } from '../../ui';
import { contactsFromCsv, previewRows, importBatches, IMPORT_MAX_ROWS } from '../../utils/contactCsv';
import './Contacts.css';

const TEMPLATE = 'name,email,phone,company,tags\nAda Lovelace,ada@example.com,07700 900123,Analytical Ltd,Client; Partner\n';
const SHOWN = 100;
const plural = (n, one, many = `${one}s`) => `${n.toLocaleString('en-GB')} ${n === 1 ? one : many}`;

export default function ImportContacts({ open, onClose, contacts, onImported }) {
  const [file, setFile] = useState(null);       // { name, rows, columns, problems }
  const [readError, setReadError] = useState('');
  const [progress, setProgress] = useState(null); // { done, total }
  const [result, setResult] = useState(null);   // { created, skipped, failed, failures, stoppedAt, error }
  const inputRef = useRef(null);

  const preview = useMemo(() => (file ? previewRows(file.rows, contacts) : []), [file, contacts]);
  const counts = useMemo(() => ({
    new: preview.filter((r) => r.status === 'new').length,
    duplicate: preview.filter((r) => r.status === 'duplicate').length,
    fail: preview.filter((r) => r.status === 'fail').length,
  }), [preview]);

  const reset = () => { setFile(null); setReadError(''); setProgress(null); setResult(null); if (inputRef.current) inputRef.current.value = ''; };
  const close = () => { if (progress) return; reset(); onClose(); };

  function onFile(e) {
    const f = e.target.files?.[0];
    setReadError(''); setResult(null);
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { setReadError('That file is over 5 MB. Split it, or keep just the columns Aidey imports.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = contactsFromCsv(String(reader.result || ''));
      if (!parsed.rows.length && !parsed.problems.length) parsed.problems.push('The file has a header row but no contacts under it.');
      setFile({ name: f.name, ...parsed });
    };
    reader.onerror = () => setReadError('That file could not be read. Is it a CSV?');
    reader.readAsText(f);
  }

  async function runImport() {
    const rows = file.rows;
    const batches = importBatches(rows);
    const total = { created: 0, skipped: 0, failed: 0, failures: [] };
    let offset = 0;
    setProgress({ done: 0, total: batches.length });
    for (let i = 0; i < batches.length; i += 1) {
      try {
        const res = await api('/api/contacts/import', { method: 'POST', body: JSON.stringify({ rows: batches[i] }) });
        const d = await res.json();
        if (typeof d?.created !== 'number') throw new Error('The server did not say what it imported.');
        total.created += d.created; total.skipped += d.skipped; total.failed += d.failed;
        total.failures.push(...(d.failures || []).map((f) => ({ ...f, row: f.row + offset })));
      } catch (err) {
        setProgress(null);
        setResult({ ...total, stoppedAt: offset, rows: rows.length, error: messageFromError(err, 'The import stopped.') });
        if (total.created) onImported?.();
        return;
      }
      offset += batches[i].length;
      setProgress({ done: i + 1, total: batches.length });
    }
    setProgress(null);
    setResult({ ...total, rows: rows.length });
    if (total.created) onImported?.();
  }

  const tooMany = file && file.rows.length > IMPORT_MAX_ROWS;
  const blocked = !file || file.problems.length > 0 || tooMany || counts.new === 0;

  let footer;
  if (result) {
    footer = <><Button variant="tertiary" onClick={reset}>Import another file</Button><Button variant="confirm" onClick={close}>Done</Button></>;
  } else if (file) {
    footer = <>
      <Button variant="tertiary" onClick={reset} disabled={!!progress}>Choose another file</Button>
      <Button variant="confirm" onClick={runImport} disabled={blocked || !!progress} aria-busy={!!progress || undefined}>
        {progress ? `Importing… ${progress.done} of ${progress.total}` : `Import ${plural(counts.new, 'contact')}`}
      </Button>
    </>;
  } else {
    footer = <Button variant="tertiary" onClick={close}>Cancel</Button>;
  }

  return (
    <Modal open={open} onClose={close} title="Import contacts from CSV" color="pastel-green" width={720} footer={footer} closeOnBackdrop={!progress}>
      {!file && !result && (
        <div className="contacts-import__pick">
          <p className="contacts-help">
            The first row names the columns: <strong>name</strong>, <strong>email</strong>, <strong>phone</strong>, <strong>company</strong> and <strong>tags</strong> (separate tags with a semicolon). Only name is required. Up to {IMPORT_MAX_ROWS.toLocaleString('en-GB')} rows.
          </p>
          <p className="contacts-help">Anyone whose email is already in your contacts is skipped, so importing the same file twice adds nobody twice.</p>
          <label className="contacts-file">
            <input ref={inputRef} type="file" accept=".csv,text/csv" onChange={onFile} data-testid="contacts-import-file" />
            <span className="ui-btn ui-btn--create">Choose a CSV file</span>
          </label>
          <a className="contacts-link" download="aidey-contacts-template.csv" href={`data:text/csv;charset=utf-8,${encodeURIComponent(TEMPLATE)}`}>Download a template</a>
          {readError && <p className="contacts-error" role="alert">{readError}</p>}
        </div>
      )}

      {file && !result && (
        <>
          <p className="contacts-help"><strong>{file.name}</strong>: {plural(file.rows.length, 'row')}</p>
          {file.problems.map((p) => <Banner key={p} tone="problem">{p}</Banner>)}
          {!file.problems.length && (
            <div className="contacts-import__counts" data-testid="contacts-import-counts">
              <StatusPill tone="success">{plural(counts.new, 'new contact')}</StatusPill>
              <StatusPill tone="neutral">{plural(counts.duplicate, 'duplicate')} skipped</StatusPill>
              {counts.fail > 0 && <StatusPill tone="warning">{plural(counts.fail, 'row')} cannot be imported</StatusPill>}
            </div>
          )}
          {!file.problems.length && preview.length > 0 && (
            <div className="contacts-import__table" role="table" aria-label="Rows in this file">
              <div className="contacts-import__tr contacts-import__th" role="row">
                <span role="columnheader">Name</span><span role="columnheader">Email</span><span role="columnheader">Company</span><span role="columnheader">Tags</span><span role="columnheader">Will</span>
              </div>
              {preview.slice(0, SHOWN).map((r, i) => (
                <div className="contacts-import__tr" role="row" key={i}>
                  <span role="cell" className="contacts-import__name">{r.name || <em className="contacts-muted">No name</em>}</span>
                  <span role="cell" className="contacts-import__email">{r.email}</span>
                  <span role="cell">{r.company}</span>
                  <span role="cell">{r.tags.join(', ')}</span>
                  <span role="cell">
                    {r.status === 'new' && <StatusPill tone="success">Add</StatusPill>}
                    {r.status === 'duplicate' && <StatusPill tone="neutral">Skip, {r.reason.toLowerCase()}</StatusPill>}
                    {r.status === 'fail' && <StatusPill tone="warning">{r.reason}</StatusPill>}
                  </span>
                </div>
              ))}
              {preview.length > SHOWN && <p className="contacts-help contacts-import__more">and {plural(preview.length - SHOWN, 'more row')}</p>}
            </div>
          )}
        </>
      )}

      {result && (
        <div className="contacts-import__result" data-testid="contacts-import-result">
          {result.error && (
            <Banner tone="problem">
              The import stopped after {plural(result.stoppedAt, 'row')} of {result.rows.toLocaleString('en-GB')}: {result.error} The rest were not imported; importing the file again skips everyone already added.
            </Banner>
          )}
          <p className="contacts-import__headline">
            {result.created ? `${plural(result.created, 'contact')} added.` : 'No contacts were added.'}
          </p>
          <ul className="contacts-import__list">
            <li>{plural(result.skipped, 'duplicate')} skipped (already a contact, or repeated in the file)</li>
            <li>{plural(result.failed, 'row')} could not be imported</li>
          </ul>
          {result.failures.length > 0 && (
            <ul className="contacts-import__list contacts-muted">
              {result.failures.slice(0, 20).map((f) => <li key={f.row}>Row {f.row}: {f.reason}</li>)}
            </ul>
          )}
        </div>
      )}
    </Modal>
  );
}
